  <template>
    <div v-if="isOpen" class="cart-modal-overlay" @click.self="closeModal">
      <aside class="cart-modal" role="dialog" aria-modal="true">
        <header class="cart-header">
          <div>
            <h2 class="cart-title">Your cart</h2>
            <p class="cart-subtitle">{{ items.length }} item{{ items.length === 1 ? '' : 's' }}</p>
          </div>
          <button class="close-btn" @click="closeModal" aria-label="Close cart">&times;</button>
        </header>

        <div class="cart-body">
          <div v-if="items.length" class="cart-banner">
            <p v-if="isFreeDigital && digitalSelected" class="w-full">
              <span class="banner-title">Digital images</span>
              Complimentary downloads for this ceremony. We'll prepare them once you click
              <span class="font-semibold">{{ primaryButtonLabel }}</span>.
            </p>
            <p v-else-if="showDigitalInfo" class="w-full">
              <span class="banner-title">Digital images</span>
              1st image: {{ isAdelaide ? 'AUD$40' : '£20' }}. Additional: {{ isAdelaide ? 'AUD$30' : '£15' }} each.
            </p>
            <p v-else class="w-full">
              <span class="banner-title">Print images</span>
              From {{ isAdelaide ? 'AUD$30' : '£15' }} each. Frames available at checkout on the day.
            </p>
          </div>

          <div v-else class="empty-state">
            <p class="empty-title">Your cart is empty</p>
            <p class="empty-subtitle">Pick a few snapshots to get started.</p>
          </div>

          <div class="cart-items">
            <div
              v-for="(image, index) in items"
              :key="image.id || image.src || index"
              class="cart-item"
            >
              <img :src="image.src" class="item-thumbnail" alt="Selected snapshot" />
              <div class="item-info">
                <div class="item-title">Snapshot {{ index + 1 }}</div>
                <div class="item-meta">
                  <span v-if="image.isDigitalSelected" class="pill">Digital</span>
                  <span v-if="image.isPrintSelected" class="pill pill-outline">Print</span>
                </div>
                <button class="remove-link" @click="removeItem(image)">Remove</button>
              </div>
            </div>
          </div>
        </div>

        <footer class="cart-footer">
          <div class="summary-row" v-if="digitalSelected && !isFreeDigital">
            <span>Total</span>
            <span class="summary-total">{{ isAdelaide ? 'AUD$' : '£' }}{{ cartTotal }}</span>
          </div>
          <p v-else-if="digitalSelected && isFreeDigital" class="summary-free">
            Digital images are free for this ceremony.
          </p>
          <button class="primary-btn" @click="purchaseButtonClicked">
            {{ primaryButtonLabel }}
          </button>
          <button
            v-if="items.length > 0 && !this.isSharedBasket()"
            class="secondary-btn"
            @click="shareButtonClicked"
          >
            {{ shareBasketText }}
          </button>
          <p class="fine-print">
            Note: Print purchases can be made on graduation day at our photo desk. You will receive a
            reference code for the images selected here.
          </p>
        </footer>
      </aside>

      <ShareBasketFormModal
        v-if="isShareBasketModalVisible"
        @submitModal="submitFormModal"
        @close="hideFormModal"
      />

      <QRCodeShareModal
        v-if="isQRCodeShareModalVisible"
        :shareBasketUrl="basketUrl"
        @submitModal="submitFormModal"
        @close="hideQRCodeModal"
      />

    </div>
  </template>
  
  <script>
  import QRCodeShareModal from './QRCodeShareModal.vue';
  import ShareBasketFormModal from './ShareBasketFormModal.vue';
  import { extend } from 'vee-validate';
  import { getSnapshotConfig } from '../../snapshotConfig';
  import {
    required,
    min,
    max,
    email,
    regex,
    confirmed
  } from "vee-validate/dist/rules"

  extend("personal_email", {
    message: (field) =>
      "The " +
      field +
      " value is not a personal email, please enter an email not ending in ac.uk.",
    validate: (value) => !/^.*((\.ac\.uk)|(bpp\.com))$/.test(value),
  });

  extend("required", {
    ...required,
    message: "This field is required",
  });
  extend("min", min);
  extend("confirmed", {
    ...confirmed,
    message: "Your email addresses do not match",
  });
  extend("regex", regex);
  extend("max", max);
  extend("email", email);

  export default {
    name: "CartModal",
  props: {
    isOpen: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    basketUrl: String,
    isFreeDigital: { type: Boolean, default: false },
  },
    components: {
      ShareBasketFormModal,
      QRCodeShareModal,
    },
    data() {
      return {
        isShareBasketModalVisible: false,
        isQRCodeShareModalVisible: false,
        shareBasketText: 'Share your Basket',
        form: {
          name: "",
          email: null,
        }
      }
    },
    watch: {
      basketUrl(url) {
        this.newBasketShareLink(url);
      }
    },
   
    computed: {
      snapshotConfig() {
        return getSnapshotConfig(this.$route.params.uni);
      },
      isAdelaide() {
        return this.snapshotConfig.pricingTier === 'aud';
      },
      showDigitalInfo() {
        if (typeof this.$route.query.printOnly === undefined) {
          return true;
        }
        else {
          if (this.$route.query.printOnly == "true") {
            return false;
          }
          else {
            return true;
          }
        } 
      },
      isOnlyPrints() {
        return this.items.every(item => item.isPrintSelected && !item.isDigitalSelected)
      },
      digitalSelected() {
        return this.items.some(item => item.isDigitalSelected)
      },
      digitalCount() {
        return this.items.filter(item => item.isDigitalSelected).length;
      },
      printCount() {
        return this.items.filter(item => item.isPrintSelected).length;
      },
      cartTotal() {
        var digitals_selected = 0
        // For each image, add cost if digital is selected
        // (first digital=£20, subsequent=£15, etc.)
        this.items.forEach((img) => {
          if (img.isDigitalSelected) {
            digitals_selected += 1;
          }
        });
        if (digitals_selected == 0) {
          return 0;
        }

        if (this.isFreeDigital) {
          return 0;
        }

        if (this.isAdelaide) {
          // First digital = AUD$40, subsequent = AUD$30
          return ((digitals_selected - 1) * 30) + (40);
        } else {
          // First digital = £20, subsequent = £15
          return ((digitals_selected - 1) * 15) + (20);
        }
      },
      primaryButtonLabel() {
        if (this.isOnlyPrints) {
          return 'Submit selection and pay in person';
        }
        if (this.isFreeDigital) {
          return 'Download my images';
        }
        return 'Pay Online for Digital';
      },
    },
    methods: {
      closeModal() {
        this.$emit("close");
        this.shareBasketText = 'Share your basket'
      },
      removeItem(image) {
        this.$emit("remove", image);
      },
      checkItemsBeforeEmmitting(items) {
        let errors = 0;
        items.forEach((item) => {
          // Check if delivery method have been selected
          if(item.isDigitalSelected == false && item.isPrintSelected == false){
            errors++
          }
        });
        return errors
      },      
      isSharedBasket(){
        return !!this.$route.query.shareBasket;
      },
      purchaseButtonClicked() {
        let errorsCaught = this.checkItemsBeforeEmmitting(this.items);
        if(errorsCaught >= 1){
          return
        }
        if(this.isSharedBasket()){
          // Get name and Email
          this.isShareBasketModalVisible = true;
        } else {
          // Good to submit
          this.$emit("submit", this.items);
        }
     
      },
      shareButtonClicked() {
        this.$emit("share", this.items)
        // Always open QR modal when share btn is clicked
        this.isQRCodeShareModalVisible = true;
      },
      newBasketShareLink(url){
        this.basketUrl = url;
        // Copy to clipboard
        if(navigator.clipboard){
          // TODO: User selecting share with the same items can be problematic
          // shareBasketText will not update
          navigator.clipboard.writeText(url)
            .then(() =>{
              this.shareBasketText = 'Copied to clipboard';
              setTimeout(() => {
                this.shareBasketText = 'Share your Basket';
              }, 3000);
            })
            .catch(() => {
              this.shareBasketText = 'Failed to generate URL';
            });
        }
      },
      submitFormModal(formData) {
        // Good to submit for checkout
        this.$emit("submit", this.items, formData);
      },
      hideFormModal(){
        this.isShareBasketModalVisible = false;
      },
      hideQRCodeModal(){
        this.isQRCodeShareModalVisible = false;
      }
    },
  };
  </script>
  
  <style scoped>
.cart-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  z-index: 2000;
}

.cart-modal {
  background: #ffffff;
  width: min(480px, 94vw);
  height: 100%;
  border-radius: 16px 0 0 16px;
  box-shadow: -16px 0 40px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
}

.cart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px 12px;
  border-bottom: 1px solid #eef2f7;
}

.cart-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.cart-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
}

.close-btn {
  border: none;
  background: #f1f5f9;
  color: #111827;
  font-size: 22px;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  cursor: pointer;
}

.cart-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 20px;
}

.cart-banner {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.92rem;
  margin-bottom: 16px;
}

.banner-title {
  font-weight: 700;
  display: block;
  margin-bottom: 4px;
}

.empty-state {
  text-align: center;
  padding: 40px 16px 24px;
  color: #6b7280;
}

.empty-title {
  font-weight: 700;
  color: #111827;
}

.empty-subtitle {
  font-size: 0.9rem;
}

.cart-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cart-item {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: #ffffff;
}

.item-thumbnail {
  width: 82px;
  height: 82px;
  object-fit: cover;
  border-radius: 10px;
  background: #f8fafc;
}

.item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.item-title {
  font-weight: 600;
  color: #111827;
}

.item-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pill {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #ecfeff;
  color: #0e7490;
}

.pill-outline {
  background: #f8fafc;
  color: #1f2937;
  border: 1px solid #e2e8f0;
}

.remove-link {
  background: transparent;
  border: none;
  color: #ef4444;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0;
  cursor: pointer;
  align-self: flex-start;
}

.cart-footer {
  padding: 16px 20px 20px;
  border-top: 1px solid #eef2f7;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  color: #111827;
}

.summary-total {
  font-size: 1.1rem;
}

.summary-free {
  color: #166534;
  font-weight: 600;
}

.primary-btn {
  background: #0f766e;
  color: #ffffff;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  padding: 12px 16px;
  cursor: pointer;
}

.primary-btn:hover {
  background: #0d6d66;
}

.secondary-btn {
  background: #ffffff;
  color: #0f766e;
  border: 1px solid #0f766e;
  font-weight: 600;
  border-radius: 12px;
  padding: 10px 14px;
  cursor: pointer;
}

.fine-print {
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.3;
}

@media (max-width: 640px) {
  .cart-modal {
    width: 100%;
    border-radius: 0;
  }
}
  </style>
  
